import React, { useState } from 'react';
import ToolLayout from '../../components/ToolLayout';
import UsageIndicator from '../../components/UsageIndicator';
import ModeSelector from '../../components/ModeSelector';
import MarkdownRenderer from '../../components/MarkdownRenderer';
import DeepResearchProgress from '../../components/DeepResearchProgress';
import { Building2, Target, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../lib/firebase';
import { authenticatedApiCall } from '../../lib/apiClient';
import { useUser } from '@/context/UserContext';
import { emitUsageRefresh } from '@/lib/usageEvents';

const CompetitorAnalysis = () => {
  const [yourBrand, setYourBrand] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [industry, setIndustry] = useState('');
  const [result, setResult] = useState('');
  const [sources, setSources] = useState([]); // ✅ ADDED
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('basic');

  const { plan, loading: userLoading, authReady } = useUser();

  if (userLoading || !authReady) {
    return (
      <ToolLayout title="Competitor Analysis">
        <div className="text-center py-16 text-white">Loading…</div>
      </ToolLayout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!auth.currentUser) {
      toast.error('Please login to use this tool');
      return;
    }

    setLoading(true);
    setResult('');
    setSources([]);

    try {
      const data = await authenticatedApiCall(
        '/api/generate-competitor',
        { yourBrand, competitor, industry, mode }
      );

      setResult(data.analysis || '');
      setSources(data.sources || []);

      emitUsageRefresh({ tool: 'competitor', mode });
      toast.success('Analysis generated');
    } catch (err) {
      toast.error('Failed to generate analysis');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolLayout title="Competitor Analysis">
      <div className="max-w-4xl mx-auto">
        <UsageIndicator />

        <ModeSelector
          mode={mode}
          setMode={setMode}
          subscription={{ plan, status: 'active' }}
        />

        {mode === 'deep' && loading && (
          <DeepResearchProgress isActive={loading} />
        )}

        <div className="rounded-2xl p-6 mb-8 bg-accent-teal/10 border border-accent-teal/30">
          <div className="flex gap-4">
            <Sparkles className="text-accent-teal mt-1" />
            <div>
              <h2 className="text-xl font-bold text-white mb-2">How it works</h2>
              <p className="text-neutral-silver">
                Compare your brand against a competitor with a structured SWOT-style
                analysis, positioning insights, and recommendations.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 mb-8">
          <Input
            label="Your Brand / Product"
            icon={Building2}
            value={yourBrand}
            onChange={setYourBrand}
            placeholder="e.g. NT Studios"
          />
          <Input
            label="Competitor"
            icon={Target}
            value={competitor}
            onChange={setCompetitor}
            placeholder="e.g. Competing agency or SaaS"
          />
          <Input
            label="Industry / Niche"
            value={industry}
            onChange={setIndustry}
            placeholder="e.g. Web design & branding"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-lg bg-accent-teal text-white font-semibold disabled:opacity-50"
          >
            {loading ? 'Analyzing…' : 'Generate Analysis'}
          </button>
        </form>

        {result && (
          <div className="rounded-2xl p-6 bg-white/5 border border-accent-teal/20">
            <h2 className="text-2xl font-bold text-white mb-4">
              Competitor Analysis
            </h2>

            <MarkdownRenderer content={result} />

            {mode === 'deep' && sources.length > 0 && (
              <div className="mt-6 border-t border-white/10 pt-4">
                <h3 className="text-sm font-semibold text-white mb-2">
                  Sources
                </h3>
                <ul className="space-y-1 text-sm">
                  {sources.map((s, i) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-teal underline"
                      >
                        {s.title || s.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </ToolLayout>
  );
};

const Input = ({ label, value, onChange, icon: Icon, placeholder }) => (
  <div>
    <label className="text-white font-medium mb-2 flex gap-2">
      {Icon && <Icon size={18} />} {label}
    </label>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-lg bg-white/5 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-accent-teal"
      required
    />
  </div>
);

export default CompetitorAnalysis;
